import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import { autoinject, bind, componentAutoReaction, inject, NothingHere } from 'Mri.Web.Common';
import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { AccountsStore } from '../../Accounts/AccountsStore';
import { ContractsStore } from '../../Contracts/ContractsStore';
import { AppStore } from '../../Core/AppStore';
import { PartsStore } from '../../Parts/PartsStore';
import { WeatherStore } from '../../Weather/WeatherStore';
import { DisplaysStore } from '../DisplaysStore';
import { DisplayStatsCards } from './StatusCards/DisplayStatsCards';
import { DisplaySoftwareUpdateStatusCard } from './StatusCards/DisplayStoftwareUpdateStatusCard';
import { DisplayStatusTopCards } from './StatusCards/DisplayStatusTopCards';
import { UnitsStore } from '../../Units/UnitsStore';
import { DisplayGroupsStore } from '../../DisplayGroups/DisplayGroupsStore';
import { BsTv } from '@react-icons/all-files/bs/BsTv';

export interface IDisplayStatusParams {
    serialNumber: string;
}
export interface IDisplayStatusProps extends RouteComponentProps<IDisplayStatusParams> {}

@autoinject
@observer
export class DisplayStatus extends React.Component<IDisplayStatusProps> {
    public async componentDidMount() {
        componentAutoReaction(this, () => this.props.match.params.serialNumber, this.loadAsync);
    }

    public render() {
        return (
            <div className="container-fluid pt-3">
                {this.display && (
                    <div className="row px-2">
                        <DisplaySoftwareUpdateStatusCard display={this.display} loading={this.loading} />

                        <div className="col-12 px-3">
                            <DisplayStatusTopCards display={this.display} statusPoll={this.statusPoll} />
                        </div>

                        <DisplayStatsCards display={this.display} history={this.props.history} loading={this.loading} />

                        <NothingHere
                            empty={!this.display}
                            icon={<BsTv />}
                            label="No Current Status Poll Could Be Retrieved"
                            loading={this.loading}
                            loadingMessage="Loading Display..."
                        />
                    </div>
                )}
            </div>
        );
    }

    @computed
    private get display() {
        return this.displaysStore.displays.get(this.props.match.params.serialNumber);
    }

    @bind
    @action
    private async loadAsync() {
        this.loading = true;

        const { serialNumber } = this.props.match.params;

        try {
            await this.displaysStore.loadAsync(serialNumber, {
                params: {
                    status: true,
                    contract: true,
                    displayGroups: true,
                },
            });

            if (!this.display) throw new Error(`${serialNumber} does not exist`);

            const promises: Promise<any>[] = [];

            if (this.display!.contract)
                promises.push(this.contractsStore.loadAsync(this.display!.contract, true).catch(console.error));

            promises.push(this.displayGroupsStore.loadAllAsync().catch(console.error));
            promises.push(this.displaysStore.loadCurrentDisplayEventsAsync(serialNumber).catch(console.error));
            promises.push(this.displaysStore.loadDisplayEventProfilesAsync(serialNumber).catch(console.error));
            promises.push(this.displaysStore.loadCurrentStatusAsync(serialNumber).catch(console.error));
            promises.push(this.displaysStore.loadOpenTicketsAsync(serialNumber).catch(console.error));
            promises.push(this.displaysStore.loadSoftwareVersionsAsync(serialNumber).catch(console.error));
            promises.push(this.displaysStore.loadLatestScreenSnapshotAsync(serialNumber).catch(console.error));
            promises.push(this.weatherStore.loadWeatherAsync(serialNumber).catch(console.error));
            promises.push(this.accountsStore.loadAllAsync(true).catch(console.error));
            promises.push(this.displaysStore.loadSoftwareUpdateStatusAsync(serialNumber).catch(console.error));
            promises.push(this.displaysStore.getLatestTelemetryPollAsync(serialNumber).catch(console.error));
            promises.push(
                this.partsStore
                    .loadPartsFromGuid(this.display.unitPartGuid ?? this.display.partGuid, true)
                    .catch(console.error)
            );
            promises.push(this.displaysStore.loadWarehouseAsync(serialNumber).catch(console.error));
            if (!this.display.virtualUnit) {
                promises.push(this.unitsStore.loadAsync(this.display.unitSerialNumber).catch(console.error));
            }

            await Promise.all(promises);

            if (!this.display.virtualUnit) await this.loadUnitDisplaysAsync();
        } catch (e: any) {
            this.appStore.setError(e);
        } finally {
            this.loading = false;
        }
    }

    @bind
    @action
    private async loadUnitDisplaysAsync() {
        if (!this.display) return;
        if (this.display.virtualUnit) return;

        const promises: Promise<any>[] = [];
        const unit = this.unitsStore.units.get(this.display.unitSerialNumber);
        const unitDisplays = unit?.displays.filter(d => d != this.display!.serialNumber);

        if (unitDisplays) {
            for (const display of unitDisplays) {
                await this.displaysStore
                    .loadAsync(display, {
                        params: {
                            status: true,
                            contract: true,
                            displayGroups: true,
                        },
                    })
                    .catch(console.error);
            }
        }

        await Promise.all(promises);
    }

    @computed
    private get activeDisplayStatus() {
        if (this.display) return this.displaysStore.statuses.get(this.display.serialNumber);
    }

    @computed
    private get statusPoll() {
        if (this.activeDisplayStatus) return this.activeDisplayStatus.lastKnownPoll.displayStatusPoll;
    }

    @observable
    private loading = false;
    @inject
    private appStore!: AppStore;
    @inject
    private accountsStore!: AccountsStore;
    @inject
    private contractsStore!: ContractsStore;
    @inject
    private unitsStore!: UnitsStore;
    @inject
    private displaysStore!: DisplaysStore;
    @inject
    private displayGroupsStore!: DisplayGroupsStore;
    @inject
    private partsStore!: PartsStore;
    @inject
    private weatherStore!: WeatherStore;
}
